import { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowRight,
  ExternalLink,
  Globe,
  ShoppingCart,
  Palette,
  Wrench,
  Gamepad2Icon,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";

const projects = [
  {
    id: 1,
    title: "Léger's Coffee",
    category: "Site vitrine",
    description:
      "Site vitrine et catalogue digital pour un coffee shop mettant en avant l’univers gourmand de l’enseigne, ses produits faits maison, ses repas du jour, ses afterworks et sa newsletter.",
    image:
      "images/legers-coffee.com.png",
    tags: ["Restaurant", "Catalogue", "Newsletter"],
    icon: Globe,
    results: "Mise en valeur de l’offre et de l’expérience client",
    url: "https://legers-coffee.com",
    slug: "legers-coffee",
  },
  {
    id: 2,
    title: "Weblax's Studio",
    category: "Site vitrine",
    description:
      "Site corporate de l’agence Weblax présentant son positionnement, son équipe, ses services digitaux et sa vision autour de solutions web performantes, accessibles et évolutives.",
    image:
      "images/weblax.fr.png",
    tags: ["Agence", "Corporate", "Services"],
    icon: Globe,
    results: "Présentation claire de l’expertise et des services",
    url: "https://weblax.fr",
    slug: "weblax-studio",
  },
  {
    id: 3,
    title: "Baume Shop",
    category: "E-commerce",
    description:
      "Boutique en ligne spécialisée dans le bien-être féminin, avec une offre structurée autour du cycle menstruel, de l’intimité, de la maternité, de la ménopause et d’ateliers thématiques.",
    image:
      "images/baume-shop.com.png",
    tags: ["E-commerce", "Bien-être", "Catalogue produit"],
    icon: ShoppingCart,
    results: "Catalogue e-commerce riche et segmenté",
    url: "https://baume-shop.com",
    slug: "baume-shop",
  },
  {
    id: 4,
    title: "Atelier du Pendu",
    category: "Site vitrine",
    description:
      "Le confort et l'esthétisme sont les deux piliers d'une sellerie de qualité. Notre engagement pour chaque projet est total pour vous assurer une finition à la hauteur de vos attentes.",
    image:
      "images/sellerie-atelierdupendu.fr.png",
    tags: ["Artisanat", "Vitrine", "Sellerie"],
    icon: Palette,
    results: "Présence web en évolution",
    url: "https://sellerie-atelierdupendu.fr",
    slug: "atelier-du-pendu",
  },
  {
    id: 5,
    title: "Brasserie Carougeoise",
    category: "Site vitrine",
    description:
      "Site de restaurant gastronomique à Carouge mettant en avant l’identité de la brasserie, ses cartes par moment de consommation, la réservation de table, la privatisation et les informations d’accès.",
    image:
      "images/brasseriecarougeoise-dev.weblax.fr.png",
    tags: ["Restaurant", "Réservation", "Hospitalité"],
    icon: Globe,
    results: "Valorisation de l’univers culinaire et de la réservation",
    url: "https://brasseriecarougeoise-dev.weblax.fr/",
    status: "dev",
    slug: "brasserie-carougeoise",
  },
  {
    id: 6,
    title: "Santé Équilibre",
    category: "Site vitrine",
    description:
      "Site professionnel pour Julien Durand, axé sur le bien-être, l’acupuncture traditionnelle et les massages thérapeutiques, avec mise en avant des soins, des certifications et de la prise de rendez-vous.",
    image:
      "images/sante-equilibre.ch.png",
    tags: ["Santé", "Bien-être", "Prise de rendez-vous"],
    icon: Wrench,
    results: "Mise en avant de l’expertise et des services de santé",
    url: "https://sante-equilibre.ch",
    slug: "sante-equilibre",
  },
  {
    id: 7,
    title: "Alma Fleur SA",
    category: "E-commerce",
    description:
      "Boutique en ligne de fleurs et plantes, avec une offre diversifiée incluant bouquets, plantes d’intérieur, compositions florales pour événements et abonnements floraux.",
    image:
      "images/almafleur-dev.weblax.fr.png",
    tags: ["E-commerce", "Fleurs", "Abonnements"],
    icon: ShoppingCart,
    results: "Une visbilité digital renforcée ainsi qu'un catalogue e-commerce riche",
    url: "https://almafleur-dev.weblax.fr/",
    status: "dev",
    slug: "alma-fleur-sa",
  },
  {
    id: 8,
    title: "Les Noces de Gingko",
    category: "E-commerce",
    description:
      "Votre artisan fleuriste à Martigny, Verbier et Sion, avec une boutique en ligne proposant une large gamme de fleurs, plantes et compositions florales pour toutes les occasions.",
    image:
      "images/nocesdeginkgo-dev.weblax.fr.png",
    tags: ["E-commerce", "Fleurs", "Compositions"],
    icon: ShoppingCart,
    results: "Catalogue e-commerce riche et attractif et présence digitale renforcée",
    url: "https://nocesdeginkgo-dev.weblax.fr/",
    status: "dev",
    slug: "les-noces-de-gingko",
  },
  {
    id: 9,
    title: "PlayRyvenox Gaming",
    category: "Gaming",
    description:
      "Site de gaming pour la communauté PlayRyvenox. Farmez des ressources, échangez avec d'autres joueurs et participez à des événements exclusifs pour améliorer votre expérience de jeu.",
    image:
      "images/playryvenox.com.png",
    tags: ["Gaming", "Communauté", "Événements"],
    icon: Gamepad2Icon,
    results: "Expérience de jeu améliorée et communauté renforcée",
    url: "https://playryvenox.com/",
    status: "dev",
    slug: "playryvenox-gaming",
  },
];

export const Portfolio = () => {
  const [activeCategory, setActiveCategory] = useState("Tous");

  const pageTitle =
    "Réalisations Weblax | Exemples de sites internet, e-commerce et projets web";
  const pageDescription =
    "Découvrez les réalisations Weblax : sites vitrines, boutiques e-commerce et projets digitaux conçus pour améliorer la visibilité, l’expérience utilisateur et la conversion.";
  const pageUrl = "https://weblax.fr/realisations";
  const pageImage = "https://weblax.fr/og-image.jpg";

  const categories = [
    { name: "Tous", value: "Tous", count: projects.length },
    {
      name: "Sites vitrines",
      value: "Site vitrine",
      count: projects.filter((p) => p.category === "Site vitrine").length,
    },
    {
      name: "E-commerce",
      value: "E-commerce",
      count: projects.filter((p) => p.category === "E-commerce").length,
    },
    {
      name: "Gaming",
      value: "Gaming",
      count: projects.filter((p) => p.category === "Gaming").length,
    },
  ];

  const filteredProjects =
    activeCategory === "Tous"
      ? projects
      : projects.filter((p) => p.category === activeCategory);

  const collectionPageStructuredData = {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "Réalisations Weblax",
    url: pageUrl,
    description: pageDescription,
  };

  const itemListStructuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: projects.map((project, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: project.title,
      url: `https://weblax.fr/realisations#${project.slug}`,
    })),
  };

  const organizationStructuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Weblax",
    url: "https://weblax.fr",
    logo: "https://weblax.fr/logo.png",
    email: "contact@weblax.fr",
    telephone: "+33685802016",
    address: {
      "@type": "PostalAddress",
      addressLocality: "Paris",
      addressCountry: "FR",
    },
  };

  return (
    <>
      <Helmet>
        <html lang="fr" />
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta
          name="keywords"
          content="réalisations agence web, portfolio création site internet, exemples site vitrine, portfolio e-commerce, projets web, agence digitale, références clients, Weblax"
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:locale" content="fr_FR" />
        <meta property="og:site_name" content="Weblax" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:image" content={pageImage} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        <script type="application/ld+json">
          {JSON.stringify(collectionPageStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(itemListStructuredData)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(organizationStructuredData)}
        </script>
      </Helmet>

      <div data-testid="portfolio-page">
        <section className="relative overflow-hidden bg-gradient-to-b from-primary/5 to-background">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(37,99,235,0.1),transparent_50%)]" />
          <div className="weblax-container relative">
            <div className="py-20 md:py-28 text-center">
              <p className="weblax-caption text-primary mb-4">Réalisations</p>
              <h1 className="weblax-heading-display mb-6 max-w-3xl mx-auto">
                Nos <span className="weblax-gradient-text">projets</span>
              </h1>
              <p className="weblax-body-lg max-w-2xl mx-auto">
                Découvrez une sélection de nos réalisations et les résultats
                obtenus pour nos clients.
              </p>
            </div>
          </div>
        </section>

        <section className="py-8 border-b border-border">
          <div className="weblax-container">
            <div className="flex flex-wrap gap-3 justify-center">
              {categories.map((category, index) => (
                <Button
                  key={index}
                  variant={
                    activeCategory === category.value ? "default" : "outline"
                  }
                  className={
                    activeCategory === category.value
                      ? "weblax-btn-primary"
                      : "weblax-btn-secondary"
                  }
                  data-testid={`filter-${category.name
                    .toLowerCase()
                    .replace(/\s/g, "-")}`}
                  onClick={() => setActiveCategory(category.value)}
                >
                  {category.name}
                  <Badge variant="secondary" className="ml-2">
                    {category.count}
                  </Badge>
                </Button>
              ))}
            </div>
          </div>
        </section>

        <section className="weblax-section">
          <div className="weblax-container">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredProjects.map((project) => (
                <Card
                  key={project.id}
                  id={project.slug}
                  className="weblax-card-interactive overflow-hidden group"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />

                    {project.status === "dev" && (
                      <span className="absolute top-3 right-3 z-10 inline-flex items-center rounded-full bg-orange-500/95 px-3 py-1 text-xs font-medium text-white shadow-md backdrop-blur-sm">
                        En cours
                      </span>
                    )}

                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="absolute bottom-4 left-4 right-4">
                        <Button
                          size="sm"
                          className="weblax-btn-primary"
                          onClick={() =>
                            window.open(
                              project.url,
                              "_blank",
                              "noopener,noreferrer"
                            )
                          }
                        >
                          Voir le projet
                          <ExternalLink className="w-4 h-4 ml-2" />
                        </Button>
                      </div>
                    </div>
                  </div>

                  <CardContent className="p-6">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 bg-primary/10 rounded-xl flex items-center justify-center">
                        <project.icon className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <h2 className="font-semibold group-hover:text-primary transition-colors">
                          <a
                            href={project.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline"
                          >
                            {project.title}
                          </a>
                        </h2>
                        <p className="text-sm text-muted-foreground">
                          {project.category}
                        </p>
                      </div>
                    </div>

                    <p className="text-sm text-muted-foreground mb-4">
                      {project.description}
                    </p>

                    <div className="flex flex-wrap gap-2 mb-4">
                      {project.tags.map((tag, index) => (
                        <Badge key={index} variant="secondary" className="text-xs">
                          {tag}
                        </Badge>
                      ))}
                    </div>

                    <div className="pt-4 border-t border-border">
                      <p className="text-sm">
                        <span className="text-muted-foreground">Résultat : </span>
                        <span className="font-medium text-primary">
                          {project.results}
                        </span>
                      </p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        <section className="weblax-section bg-card">
          <div className="weblax-container">
            <div className="bg-primary rounded-3xl p-8 md:p-12 text-center text-white relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(255,255,255,0.1),transparent_50%)]" />
              <div className="relative">
                <h2 className="text-3xl md:text-4xl font-bold mb-4">
                  Vous avez un projet similaire ?
                </h2>
                <p className="text-lg text-white/80 mb-8 max-w-xl mx-auto">
                  Discutons de votre projet et voyons comment nous pouvons vous
                  aider à atteindre vos objectifs.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/devis">
                    <Button className="bg-white text-primary hover:bg-white/90 px-8 py-3 rounded-full font-medium">
                      Demander un devis gratuit
                      <ArrowRight className="w-4 h-4 ml-2" />
                    </Button>
                  </Link>
                  <Link to="/contact">
                    <Button
                      variant="outline"
                      className="border-white/30 text-white hover:bg-white/10 px-8 py-3 rounded-full"
                    >
                      Nous contacter
                    </Button>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Portfolio;